// The Item panel's editable form state and how it is seeded from a saved item.
// Kept out of ItemDetail.tsx so the seeding rule can be unit-tested without
// rendering the whole panel.

import type {
  DayOfWeek,
  FirstExecutionKind,
  GradeGuidelineMap,
  ProItem,
  ProItemScope,
  ProMeasurementCycle,
  ProSchedule,
} from '../types/pro-item-design.types';
import { initialFirstExecution } from './item-validation';

// The N kept for a cycle that hides the 「퇴원 후 N일」 field; it is never sent for such a cycle.
export const DEFAULT_N_DAYS = 7;

// The shape of fields the user edits inline. Slices 8–11 grow this; the
// current set covers Slices 7 (basic info + scope) and 8 (schedule).
export interface ItemFormState {
  name: string;
  description: string;
  scope: ProItemScope;
  diseaseGroupId: number | null;
  diseaseCodeId: number | null;
  // Schedule (Slice 8). Stored as a flat triple plus nDays so the form is
  // trivial to control; the save layer (Slice 12) will round-trip to the
  // wire-format discriminated union `FirstExecution`.
  cycle: ProMeasurementCycle;
  daysOfWeek: DayOfWeek[];
  firstExecutionKind: FirstExecutionKind;
  firstExecutionNDays: number;
  // Grade Cutoff — SUM ("합산 점수형") only. COMPLEX mode was removed per Req
  // Clarification ADM.036 (SHCT-391), so the form no longer holds a method or
  // condition sub-state. The save payload always sends `gradeMethod: 'SUM'`.
  gradeSumG1Max: number;
  gradeSumG2Max: number;
  gradeGuidelines: GradeGuidelineMap;
}

// Seed for an item with no saved schedule yet. Mirrors the create endpoint's
// default so a brand-new DRAFT opens on a sane WEEKLY schedule.
const DEFAULT_SCHEDULE: ProSchedule = {
  cycle: 'WEEKLY',
  daysOfWeek: [],
  firstExecution: { kind: 'WEEK_AFTER_DISCHARGE' },
};

export function buildInitialForm(item: ProItem): ItemFormState {
  // A freshly-created DRAFT item has `schedule: null` from the backend; fall
  // back to defaults instead of dereferencing it (the panel used to crash here
  // with "Cannot read properties of undefined (reading 'cycle')").
  const schedule = item.schedule ?? DEFAULT_SCHEDULE;
  // Derived from the cycle, not copied: a row saved with a kind its cycle no longer allows would otherwise
  // be sent back unchanged and refused by the server, while the screen shows the derived kind.
  const firstExecution = initialFirstExecution(
    schedule.cycle,
    schedule.firstExecution ?? DEFAULT_SCHEDULE.firstExecution,
    DEFAULT_N_DAYS,
  );
  return {
    name: item.name,
    description: item.description ?? '',
    scope: item.scope,
    diseaseGroupId: item.diseaseGroupId,
    diseaseCodeId: item.diseaseCodeId,
    cycle: schedule.cycle,
    daysOfWeek: [...schedule.daysOfWeek],
    firstExecutionKind: firstExecution.kind,
    firstExecutionNDays: firstExecution.nDays,
    // COMPLEX was removed (ADM.036): a legacy item saved as COMPLEX has
    // `gradeSum: null`, so its ranges fall back to 0/0 here — validation then
    // flags them as missing, forcing the user to set real SUM ranges.
    gradeSumG1Max: item.gradeSum?.g1Max ?? 0,
    gradeSumG2Max: item.gradeSum?.g2Max ?? 0,
    gradeGuidelines: { ...item.gradeGuidelines },
  };
}
